<template>
  <v-card rounded="xl" outlined elevation="0" class="ps-datatable-card">
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="items.data"
        :search="search"
        :items-per-page="value ? options.itemsPerPage : per_page"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading')"
        :no-data-text="$t('no_data')"
        hide-default-footer
        fixed-header
        checkbox-color="primary"
        :show-select="selectable"
        v-model="internalSelectedItems"
        @input="onSelectionChange"
      >
        <template slot="progress">
          <v-progress-linear color="primary" indeterminate></v-progress-linear>
        </template>

        <template v-slot:top>
          <slot name="activator"></slot>
        </template>

        <template v-slot:item.logo="{ item }">
          <v-avatar size="40">
            <app-img :img="item.logo" />
          </v-avatar>
        </template>
        <template v-slot:item.img="{ item }">
          <div
            class="d-inline-flex position-relative"
            style="width: 40px; height: 40px"
          >
            <v-avatar tile size="40" class="rounded-lg" color="grey lighten-2">
              <app-img :img="item.img" />
            </v-avatar>
            <!-- Product type badge on top right of image -->
            <div
              v-if="item.product_type"
              :style="{
                position: 'absolute',
                top: '0',
                right: '0',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                border: '2px solid white',
                zIndex: 10,
                backgroundColor: getProductTypeColor(item.product_type),
              }"
            ></div>
          </div>
        </template>
        <template v-slot:item.invoiceStatus="{ item }">
          {{ item.status }}
        </template>
        <template v-slot:item.track_stock="{ item }">
          <span
            :class="item.track_stock ? 'green--text' : 'red--text'"
            class="text-capitalize"
          >
            {{
              item.track_stock ? $t("is_track_stock") : $t("is_not_track_stock")
            }}
          </span>
        </template>
        <template v-slot:item.orderStatus="{ item }">
          <v-chip
            small
            label
            dark
            class="text-capitalize"
            :color="orderStatusColor(item.status)"
          >
            {{ $t(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.is_online_order="{ item }">
          <v-chip
            small
            label
            dark
            class="text-capitalize"
            :color="item.is_online_order ? 'green' : 'primary'"
          >
            {{ item.is_online_order ? $t("online") : $t("counter") }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip
            small
            label
            dark
            class="text-capitalize"
            :color="orderStatusColor(item.status)"
          >
            {{ $t(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.payment_method="{ item }">
          <v-chip
            small
            label
            dark
            class="text-capitalize"
            :color="item.type === 'qr' ? 'green' : 'primary'"
          >
            {{ $t(item.type) }}
          </v-chip>
        </template>
        <template v-slot:item.queue_no="{ item }">
          <span v-if="item.queue_no" class="font-weight-medium">
            {{ formatBillQueue(item.queue_no) }}
          </span>
        </template>
        <template v-slot:item.qr_code="{ item }">
          <app-img :img="item.qr_code" width="30" height="30" />
        </template>
        <template v-slot:item.proImg="{ item }">
          <app-img :img="item.product_id.img" />
        </template>
        <template v-slot:item.index="{ index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.packName="{ item }">
          <v-chip label dark :color="packageName(item.name).color" small>
            {{ packageName(item.name).text }}</v-chip
          >
        </template>
        <template v-slot:item.blockStatus="{ item }">
          <p>
            {{ item.status }}
          </p>
        </template>

        <template v-slot:item.avatar="{ item }">
          <div
            class="d-inline-flex position-relative"
            style="width: 40px; height: 40px"
          >
            <v-avatar size="40">
              <app-img :img="IMAGES(item.avatar)" />
            </v-avatar>
            <!-- Status badge on top right of avatar (only for users with is_active property) -->
            <div
              v-if="'is_active' in item"
              :style="{
                position: 'absolute',
                top: '0',
                right: '0',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                border: '2px solid white',
                zIndex: 10,
                backgroundColor: item.is_active ? '#4caf50' : '#f44336',
              }"
            ></div>
          </div>
        </template>
        <template v-slot:item.icon="{ item }">
          <v-avatar size="33" color="#f0f0f0">
            <v-icon small>{{ item.icon }}</v-icon>
          </v-avatar>
        </template>

        <template v-slot:item.avatarUser="{ item }">
          <v-avatar size="35">
            <app-img :img="IMAGES(item.user.avatar)" />
          </v-avatar>
        </template>
        <template v-slot:item.user_stores_avatar="{ item }">
          <v-avatar size="35">
            <app-img :img="IMAGES(item.user_stores.user.avatar)" />
          </v-avatar>
        </template>

        <template v-slot:item.unit="{ item }">
          <v-chip
            label
            dark
            :color="unitColor(item.unit)"
            small
          >
            {{ $t(item.unit) }}</v-chip
          >
        </template>
        <template v-slot:item.product_unit="{ item }">
          <v-chip
            label
            dark
            :color="unitColor(item.size ? item.size.unit : '')"
            small
          >
            {{ $t(item.size ? item.size.unit : '') }}</v-chip
          >
        </template>
        <template v-slot:item.is_active="{ item }">
          <v-switch
            :input-value="item.is_active"
            @change="
              (value) => proStatus && proStatus(item, { desiredStatus: value })
            "
            :color="item.is_active ? 'primary' : 'red'"
            inset
            dense
            hide-details
            class="mt-0"
          />
        </template>
        <template v-slot:item.enable_qr_order="{ item }">
          <v-switch
            :input-value="!!item.enable_qr_order"
            @change="
              (value) =>
                toggleEnableQrOrder &&
                toggleEnableQrOrder(item, { desiredStatus: value })
            "
            :color="item.enable_qr_order ? 'primary' : 'red'"
            inset
            dense
            hide-details
            class="mt-0"
          />
        </template>
        <template v-slot:item.is_cooking="{ item }">
          <v-switch
            :input-value="!!item.is_cooking"
            @change="
              (value) =>
                toggleIsCooking && toggleIsCooking(item, { desiredStatus: value })
            "
            :color="item.is_cooking ? 'primary' : 'red'"
            inset
            dense
            hide-details
            class="mt-0"
          />
        </template>
        <template v-slot:item.is_buffet_included="{ item }">
          <v-switch
            :input-value="!!item.is_buffet_included"
            @change="
              (value) =>
                toggleBuffetIncluded &&
                toggleBuffetIncluded(item, { desiredStatus: value })
            "
            :color="item.is_buffet_included ? 'primary' : 'red'"
            inset
            dense
            hide-details
            class="mt-0"
          />
        </template>

        <template v-slot:item.packageName="{ item }">
          <v-chip
            v-if="item.package && item.package.name"
            label
            dark
            :color="packageName(item.package.name).color"
            small
          >
            {{ packageName(item.package.name).text }}</v-chip
          >
        </template>
        <template v-slot:item.branchStatus="{ item }">
          <v-btn
            @click="setBranch(item)"
            depressed
            dark
            small
            :color="branchStatus(item).color"
          >
            <v-icon dark left small>{{ branchStatus(item).icon }}</v-icon>
            {{ $t(branchStatus(item).text) }}
          </v-btn>
        </template>
        <template v-slot:item.statusUserStore="{ item }">
          <v-chip
            label
            dark
            :color="
              statusUserStore(item).status
                ? statusUserStore(item).color
                : statusUserStore(item).color
            "
            small
          >
            {{ $t(statusUserStore(item).title) }}</v-chip
          >
        </template>
        <template v-slot:item.statusBranch="{ item }">
          <v-chip label dark :color="item.status ? 'green' : 'red'" small>
            {{ item.status ? "Acitve" : "inActive" }}</v-chip
          >
        </template>
        <template v-slot:item.isDemo="{ item }">
          <v-chip label dark :color="userPackage(item.stores).color" small>
            {{ $t(userPackage(item.stores).text) }}</v-chip
          >
        </template>
        <template v-slot:item.statusUser="{ item }">
          <v-chip label dark :color="item.is_active ? 'green' : 'red'" small>
            {{ item.is_active ? $t("active") : $t("inactive") }}</v-chip
          >
        </template>
        <template v-slot:item.has_variations="{ item }">
          <span
            :class="item.has_variations ? 'green--text' : 'red--text'"
            class="text-capitalize"
          >
            {{
              item.has_variations ? $t("has_variations") : $t("no_variations")
            }}
          </span>
        </template>
        <template v-slot:item.mobileSupport="{ item }">
          <v-chip label dark :color="item.mobile ? 'green' : 'red'" small>
            {{ item.mobile ? $t("active") : $t("inactive") }}</v-chip
          >
        </template>
        <template v-slot:item.productStatus="{ item }">
          <v-chip label dark :color="orderStatusColor(item.status)" small>
            {{ $t(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.statusFB="{ item }">
          <v-chip label dark :color="item.status ? 'green' : 'red'" small>
            {{ item.status ? $t("read") : $t("not_read") }}</v-chip
          >
        </template>

        <template v-slot:item.userRole="{ item }">
          <v-chip
            dark
            :color="RoleColor(item.role)"
            small
            class="text-capitalize"
            label
          >
            {{ userRole(item.role) }}</v-chip
          >
        </template>

        <template v-slot:item.userStatus="{ item }">
          <v-chip
            small
            label
            class="text-capitalize white--text"
            :color="RoleColor(item.role)"
          >
            {{ userRole(item.role) }}</v-chip
          >
        </template>
        <template v-slot:item.handler="{ item }">
          <v-chip label :color="item.user ? 'grey' : 'red'" small>
            {{ item.user ? item.user.first_name : "No" }}</v-chip
          >
        </template>

        <template v-slot:item.typeExpense="{ item }">
          <v-chip
            label
            :color="item.type === 'income' ? 'green' : 'red'"
            small
            class="white--text"
          >
            {{ $t(item.type) }}</v-chip
          >
        </template>
        <template v-slot:item.imgCurrency="{ item }">
          <v-avatar size="35">
            <v-img :src="item.img" />
          </v-avatar>
        </template>
        <template v-slot:item.storeImg="{ item }">
          <v-avatar size="35">
            <app-img :img="item.store.logo" />
          </v-avatar>
        </template>
        <template v-slot:item.storeImgLogo="{ item }">
          <v-avatar size="35">
            <app-img :img="item.logo" />
          </v-avatar>
        </template>
        <template v-slot:item.storeImgs="{ item }">
          <v-avatar size="35">
            <app-img :img="item.stores ? item.stores.logo : ''" />
          </v-avatar>
        </template>
        <template v-slot:item.storelOGO="{ item }">
          <v-avatar size="35">
            <app-img :img="item.store.logo" />
          </v-avatar>
        </template>

        <template v-slot:item.created_at="{ item }">{{
          item.created_at | created_at
        }}</template>
        <template v-slot:item.moment="{ item }">{{
          item.created_at | moment
        }}</template>
        <template v-slot:item.category="{ item }">{{
          getCategoryName(item.category)
        }}</template>
        <template v-slot:item.date="{ item }">{{
          item.date | created_at
        }}</template>
        <template v-slot:item.orderDate="{ item }">{{
          item.created_at | moment
        }}</template>

        <template v-slot:item.deleted_at="{ item }">{{
          item.deleted_at | created_at
        }}</template>
        <template v-slot:item.history_sum_qty="{ item }">{{
          item.history_sum_qty ? item.history_sum_qty : 0
        }}</template>
        <template v-slot:item.proCreated="{ item }">{{
          item.product_id.created_at | created_at
        }}</template>
        <template v-slot:item.updated_at="{ item }">{{
          item.updated_at | created_at
        }}</template>
        <template v-slot:item.price="{ item }">{{
          item.price | NumberFormat
        }}</template>
        <template v-slot:item.tickets_sum_total_price="{ item }">{{
          item.tickets_sum_total_price | NumberFormat
        }}</template>
        <template v-slot:item.tickets_sum_total_paid="{ item }">{{
          item.tickets_sum_total_paid | NumberFormat
        }}</template>

        <template v-slot:item.sum_price_buy="{ item }">{{
          item.sum_price_buy | NumberFormat
        }}</template>
        <template v-slot:item.sum_price_sale="{ item }">{{
          item.sum_price_sale | NumberFormat
        }}</template>

        <template v-slot:item.is_show="{ item }">{{
          item.is_show ? $t("yes") : $t("no")
        }}</template>
        <template v-slot:item.is_online="{ item }">{{
          item.is_online ? $t("yes") : $t("no")
        }}</template>
        <template v-slot:item.products_sale_sum_total_price="{ item }">{{
          item.products_sale_sum_total_price | NumberFormat
        }}</template>
        <template v-slot:item.total_overdue="{ item }">{{
          item.total_overdue | NumberFormat
        }}</template>

        <template v-slot:item.history_sum_price_buy="{ item }">{{
          item.history_sum_price_buy | NumberFormat
        }}</template>
        <template v-slot:item.total_discount="{ item }">{{
          total_discount(item)
        }}</template>
        <template v-slot:item.stockPriceSale="{ item }">{{
          stockPriceSale(item.history)
        }}</template>
        <template v-slot:item.price_buy="{ item }">{{
          priceWithCurrency(item.price_buy, item.currency)
        }}</template>
        <template v-slot:item.price_sale="{ item }">{{
          priceWithCurrency(item.price_sale, item.currency)
        }}</template>
        <template v-slot:item.priceSale="{ item }">{{
          item.product_id.price_sale | NumberFormat
        }}</template>
        <template v-slot:item.total_price="{ item }">{{
          item.total_price | NumberFormat
        }}</template>
        <template v-slot:item.history_buy_sum_total_paid="{ item }">{{
          item.history_buy_sum_total_paid | NumberFormat
        }}</template>
        <template v-slot:item.total_pricePay="{ item }">{{
          item.total_price | NumberFormat
        }}</template>
        <template v-slot:item.point="{ item }">{{
          item.point | NumberFormat
        }}</template>

        <template v-slot:item.totalDiscount="{ item }">{{
          item.discount | NumberFormat
        }}</template>

        <template v-slot:item.storeName="{ item }">{{
          item.stores.name
        }}</template>
        <template v-slot:item.storesStartDate="{ item }">{{
          item.stores ? item.stores.store_id.start_date : ""
        }}</template>
        <template v-slot:item.storesEndDate="{ item }">{{
          item.stores ? item.stores.store_id.end_date : ""
        }}</template>

        <template v-slot:item.priceBuy="{ item }">{{
          item.product_id.price_buy | NumberFormat
        }}</template>
        <template v-slot:item.packagePrice="{ item }">{{
          item.package.price | NumberFormat
        }}</template>
        <template v-slot:item.name="{ item }">{{
          getProductName(item) | nameLimit
        }}</template>
        <template v-slot:item.name_en="{ item }">{{
          getProductName(item) | nameLimit
        }}</template>
        <template v-slot:item.name_th="{ item }">{{
          getProductName(item) | nameLimit
        }}</template>
        <template v-slot:item.name_cn="{ item }">{{
          getProductName(item) | nameLimit
        }}</template>
        <template v-slot:item.total="{ item }">{{
          item.total | NumberFormat
        }}</template>
        <template v-slot:item.total_price_buy="{ item }">
          {{ formatCost(item.total_price_buy) }}
        </template>
        <template v-slot:item.total_profit="{ item }">
          {{ formatProfit(item.total_profit) }}
        </template>
        <template v-slot:item.grand_total="{ item }">{{
          item.grand_total | NumberFormat
        }}</template>
        <template v-slot:item.total_amount="{ item }">{{
          item.total_amount | NumberFormat
        }}</template>
        <template v-slot:item.tax_amount="{ item }">{{
          item.tax_amount | NumberFormat
        }}</template>
        <template v-slot:item.product_count="{ item }">{{
          item.product_count | ToNumber
        }}</template>
        <template v-slot:item.user_count="{ item }">{{
          item.user_count | ToNumber
        }}</template>
        <template v-slot:item.branch_count="{ item }">{{
          item.branch_count | ToNumber
        }}</template>
        <template v-slot:item.stock="{ item }">{{
          productSize(item)
        }}</template>

        <template v-slot:item.UserRole="{ item }">
          <div v-for="(data, index) in item.roles" :key="index">
            <v-chip label small> {{ data.name }}</v-chip>
          </div>
        </template>

        <template v-slot:item.product_type="{ item }">
          <v-chip
            small
            label
            dark
            class="text-capitalize"
            :color="getProductTypeColor(item.product_type)"
          >
            {{ $t(item.product_type) }}
          </v-chip>
        </template>

        <template v-slot:item.hardware_image="{ item }">
          <v-avatar tile size="40" class="rounded-lg" color="grey lighten-2">
            <v-img
              v-if="hardwareImage(item)"
              :src="hardwareImage(item)"
              :alt="hardwareName(item)"
            />
            <v-icon v-else small color="grey lighten-1">fal fa-image</v-icon>
          </v-avatar>
        </template>
        <template v-slot:item.display_name="{ item }">
          <div class="font-weight-medium text-truncate hardware-table-name">
            {{ hardwareName(item) }}
          </div>
          <v-chip
            v-if="item.source_type === 'package'"
            x-small
            color="warning"
            outlined
            class="mt-1 rounded-lg"
          >
            {{ $t("hardware_synced_package_readonly") }}
          </v-chip>
        </template>
        <template v-slot:item.display_model="{ item }">
          <span class="text-truncate d-inline-block hardware-table-model">
            {{ hardwareModel(item) || "—" }}
          </span>
        </template>
        <template v-slot:item.category_cell="{ item }">
          <v-chip x-small color="primary" outlined class="rounded-lg">
            {{ hardwareCategory(item) }}
          </v-chip>
        </template>
        <template v-slot:item.cost_cell="{ item }">
          {{ hardwareCost(item) | NumberFormat }} {{ $t("lak") }}
        </template>
        <template v-slot:item.price_cell="{ item }">
          <span class="primary--text font-weight-bold">
            {{ hardwarePrice(item) | NumberFormat }} {{ $t("lak") }}
          </span>
        </template>

        <template slot="item.actions" slot-scope="{ item }">
          <!-- Custom Actions -->
          <template v-if="customActions">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon x-small class="rounded-lg">
                  <v-icon small>fal fa-ellipsis-h</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="(action, index) in customActions(item)"
                  :key="index"
                  @click="action.action()"
                  class="px-2"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon :color="action.color" small>{{
                      action.icon
                    }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ action.tooltip }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <!-- Default Actions -->
          <template v-else>
            <v-tooltip bottom>
              <template slot="activator" slot-scope="{ on, attrs }">
                <v-btn
                  v-if="viewItem"
                  v-bind="attrs"
                  v-on="on"
                  x-small
                  @click="viewItem(item)"
                  class="rounded-lg"
                  fab
                  depressed
                >
                  <v-icon color="blue" x-small>fal fa-eye</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("description") }}</span>
            </v-tooltip>
            <template v-if="extraActionButtons">
              <v-tooltip
                v-for="(btn, btnIndex) in extraActionButtons(item)"
                :key="`extra-action-${btnIndex}`"
                bottom
              >
                <template slot="activator" slot-scope="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    x-small
                    fab
                    depressed
                    rounded-lg
                    :color="btn.color"
                    :disabled="btn.disabled"
                    :class="btn.className || 'ml-1'"
                    @click="btn.action()"
                  >
                    <v-icon
                      x-small
                      :color="btn.whiteIcon ? 'white' : btn.iconColor"
                    >
                      {{ btn.icon }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ btn.tooltip }}</span>
              </v-tooltip>
            </template>
            <v-tooltip bottom>
              <template slot="activator" slot-scope="{ on, attrs }">
                <v-btn
                  v-if="editItem && canEditRow(item)"
                  v-bind="attrs"
                  v-on="on"
                  :class="deleteItem && canDeleteRow(item) ? 'mx-2' : 'ml-2'"
                  x-small
                  @click="editItem(item)"
                  class="rounded-lg"
                  fab
                  depressed
                >
                  <v-icon color="green" x-small>fal fa-edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("edit") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template slot="activator" slot-scope="{ on, attrs }">
                <v-btn
                  v-if="deleteItem && canDeleteRow(item)"
                  v-bind="attrs"
                  v-on="on"
                  x-small
                  @click="deleteItem(item)"
                  fab
                  class="rounded-lg"
                  depressed
                >
                  <v-icon color="red" x-small>fal fa-trash</v-icon>
                </v-btn>
              </template>
              <span>ລົບ</span>
            </v-tooltip>
          </template>
        </template>

        <template v-slot:item.actionsRole="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="viewItem"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="viewItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon color="blue" x-small>fal fa-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("description") }}</span>
          </v-tooltip>
          <v-tooltip bottom v-if="MyRole('change_password_user')">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="editItem"
                v-bind="attrs"
                v-on="on"
                :class="deleteItem ? 'mx-2' : 'ml-2'"
                x-small
                @click="editItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon color="green" x-small>fal fa-key</v-icon>
              </v-btn>
            </template>
            <span>{{ userActiveOption(item).text }}</span>
          </v-tooltip>
          <v-tooltip bottom v-if="MyRole('inactive_user')">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="deleteItem"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="deleteItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon :color="userActiveOption(item).color" x-small>{{
                  userActiveOption(item).icon
                }}</v-icon>
              </v-btn>
            </template>
            <span>{{ $t(userActiveOption(item).text) }}</span>
          </v-tooltip>
          <v-tooltip bottom v-if="MyRole('add_user_menu')">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="roleSetting"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="roleSetting(item)"
                fab
                class="rounded-lg ml-2"
                depressed
              >
                <v-icon color="primary" x-small>fal fa-user-cog</v-icon>
              </v-btn>
            </template>
            <span>ສິດທິ</span>
          </v-tooltip>
        </template>
        <template v-slot:item.product_type="{ item }">
          <v-chip
            small
            label
            dark
            class="text-capitalize"
            :color="getProductTypeColor(item.product_type)"
          >
            {{ $t(item.product_type) }}
          </v-chip>
        </template>
        <template v-slot:item.actionsProducts="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="viewItem"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="viewItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon color="blue" x-small>fal fa-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("description") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                v-if="editItem && MyRole('edit_product')"
                @click="editItem(item)"
                fab
                class="rounded-lg mx-2"
                x-small
                depressed
              >
                <v-icon x-small color="green">fal fa-edit</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("edit") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="deleteItem && MyRole('delete_product')"
                v-bind="attrs"
                v-on="on"
                @click="deleteItem(item)"
                fab
                class="rounded-lg"
                x-small
                depressed
              >
                <v-icon color="red" x-small>fal fa-trash</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("delete") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actionsBill="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="viewItem"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="viewItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon color="blue" x-small>fal fa-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("description") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="editItem && item.status !== 'WAITING_PAYMENT'"
                v-bind="attrs"
                v-on="on"
                x-small
                class="rounded-lg mx-2"
                @click="editItem(item)"
                fab
                depressed
              >
                <v-icon x-small color="green">fal fa-print</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("print") }}</span>
          </v-tooltip>

          <v-tooltip
            bottom
            v-if="
              item.status.includes('WAITING_PAYMENT') ||
              item.status.includes('cancel')
            "
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="deleteItem"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="deleteItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon x-small color="red">fal fa-trash</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("delete") }}</span>
          </v-tooltip>
          <v-tooltip
            bottom
            v-if="item.status != 'cancel' && item.status !== 'WAITING_PAYMENT'"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="deleteItem"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="deleteItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon x-small color="red">fal fa-edit</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("edit") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actionsBillUser="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                v-if="viewItem"
                x-small
                @click="viewItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon color="blue" x-small>fal fa-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("description") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                v-if="editItem"
                x-small
                class="rounded-lg ml-2"
                @click="editItem(item)"
                fab
                depressed
              >
                <v-icon color="green" x-small>fal fa-arrow-right </v-icon>
              </v-btn>
            </template>
            <span>{{ $t("report") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actionsOrder="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                v-if="viewItem"
                x-small
                @click="viewItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon color="blue" x-small>fal fa-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("description") }}</span>
          </v-tooltip>
          <v-tooltip
            v-if="
              item.status != 'decline' &&
              item.status != 'completed' &&
              item.status != 'cancel'
            "
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="editItem"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="deleteItem(item)"
                fab
                class="rounded-lg mx-2"
                depressed
              >
                <v-icon x-small color="red"> fal fa-times</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("decline") }}</span>
          </v-tooltip>
          <v-tooltip
            v-if="
              item.status != 'decline' &&
              item.status != 'completed' &&
              item.status != 'cancel'
            "
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="editItem"
                v-bind="attrs"
                v-on="on"
                x-small
                @click="editItem(item)"
                fab
                class="rounded-lg"
                depressed
              >
                <v-icon x-small color="green"> fal fa-check</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("approved") }}</span>
          </v-tooltip>
        </template>
        <template v-if="footer" v-slot:footer>
          <v-data-footer
            :options.sync="options"
            :pagination.sync="page"
            :items-per-page-options="rowsPerPageItems"
            :items-per-page-text="$t('rows_per_page')"
          >
            <template v-slot:page-text="pagetext">
              {{ footerPage(pagetext) }}
            </template>
          </v-data-footer>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { myMixin } from "@/mixin/main";
import { hasPermission } from "@/controllers/auth";
import { formatBillQueue } from "@/utils/billQueue";
import {
  displayHardwareCategory,
  localizeCatalogField,
  primaryProductImage,
  productCost,
  productPosSmartPrice,
} from "@/utils/hardwarePricing";

export default {
  props: {
    headers: { required: true, type: Array },
    footer: { type: Boolean, default: true },
    items: { required: true },
    newItem: { required: false, type: Function },
    viewItem: { required: false, type: Function },
    editItem: { required: false, type: Function },
    deleteItem: { required: false, type: Function },
    roleSetting: { required: false, type: Function },
    setBranch: { required: false, type: Function },
    proStatus: { required: false, type: Function },
    toggleEnableQrOrder: { required: false, type: Function },
    toggleIsCooking: { required: false, type: Function },
    toggleBuffetIncluded: { required: false, type: Function },
    commit: { required: false, type: String },
    customActions: { required: false, type: Function },
    extraActionButtons: { required: false, type: Function },
    canEditItem: { required: false, type: Function },
    canDeleteItem: { required: false, type: Function },
    addToBatch: { required: false, type: Function },
    selectable: { required: false, type: Boolean, default: false },
    selectedItems: { required: false, type: Array, default: () => [] },
    rowsPerPageOptions: { required: false, type: Array, default: null },
  },
  watch: {
    selectedItems: {
      handler(newVal) {
        this.internalSelectedItems = [...newVal];
      },
      immediate: true,
      deep: true,
    },
  },
  mixins: [myMixin],
  data() {
    return {
      value: false,
      isLoading: true,
      internalSelectedItems: [],
      search: "",
      options: {
        page: 1,
        itemsPerPage: 30,
      },
    };
  },
  computed: {
    ...mapGetters({
      per_page: "index/per_page",
      storeRowsPerPageItems: "index/rowsPerPageItems",
    }),
    rowsPerPageItems() {
      return this.rowsPerPageOptions || this.storeRowsPerPageItems || [15, 30, 50];
    },
    page() {
      const body = {
        page: this.items.current_page,
        itemsPerPage: this.items.per_page,
        pageStart: this.items.from,
        pageStop: this.items.to,
        pageCount: this.items.per_page,
        itemsLength: this.items.total,
      };
      return body;
    },
  },

  mounted() {
    setTimeout(() => {
      this.isLoading = false;
    }, 3000);
  },
  watch: {
    items: {
      handler(val) {
        this.isLoading = false;
      },
      deep: true,
    },

    options(next, pre) {
      // Skip initial bind — parent already loaded the first page.
      // Only refetch when the user changes page or page size.
      if (!pre) {
        return;
      }
      if (next.page === pre.page && next.itemsPerPage === pre.itemsPerPage) {
        return;
      }
      if (!this.items || !this.items.first_page_url) {
        return;
      }
      const url = Help.paginate(this.items, next, pre);
      const data = {
        url: url,
        commit: this.commit,
        that: this,
      };
      // called the pagination action
      this.fetch(data);
      this.value = true;
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    ...mapMutations({
      SET_PER_PAGE: "index/SET_PER_PAGE",
    }),
    formatBillQueue,

    IMAGES(val) {
      return Help.img(val);
    },
    canEditRow(item) {
      return this.canEditItem ? this.canEditItem(item) : true;
    },
    canDeleteRow(item) {
      return this.canDeleteItem ? this.canDeleteItem(item) : true;
    },
    hardwareImage(item) {
      return primaryProductImage(item);
    },
    hardwareName(item) {
      return localizeCatalogField(item, "name", this.$i18n?.locale || "en");
    },
    hardwareModel(item) {
      return localizeCatalogField(item, "model", this.$i18n?.locale || "en");
    },
    hardwareCategory(item) {
      return displayHardwareCategory(
        item,
        this.$t.bind(this),
        this.$te.bind(this),
      );
    },
    hardwareCost(item) {
      return productCost(item);
    },
    hardwarePrice(item) {
      return productPosSmartPrice(item);
    },
    productSize(val) {
      if (
        val != null &&
        val != undefined &&
        val.stock != null &&
        val.stock != undefined &&
        val.size != null &&
        val.size != undefined &&
        val.size.unit != null &&
        val.size.unit != undefined
      ) {
        if (val.size.unit === "pack") {
          const num = val.stock / val.size.amount;
          return `${val.stock} / ${Number(num).toFixed(1)}`;
        } else {
          return val.stock;
        }
      } else if (
        val != null &&
        val != undefined &&
        val.stock != null &&
        val.stock != undefined &&
        val.product_id != null &&
        val.product_id != undefined &&
        val.product_id.size != null &&
        val.product_id.size != undefined &&
        val.product_id.size.unit != null &&
        val.product_id.size.unit != undefined
      ) {
        if (val.product_id.size.unit === "pack") {
          const num = val.stock / val.product_id.size.amount;
          return `${val.stock} / ${Number(num).toFixed(1)}`;
        } else {
          return val.stock;
        }
      } else {
        return val.stock;
      }
    },

    footerPage(val) {
      const start = val.pageStart;
      const stop = val.pageStop;
      const total = val.itemsLength;
      return `${start > 2 ? start - 2 : start - 1} - ${stop} of ${total}`;
    },

    total_discount({ discount_type, total_discount }) {
      if (discount_type === "percentage" && total_discount) {
        return `${total_discount} %`;
      } else {
        return this.$options.filters.NumberFormat(total_discount);
      }
    },
    stockPriceSale(val) {
      const total_price =
        val
          ?.filter((item) => item?.stock && item.product.price_sale)
          .map(
            (item) =>
              (item.product.price_sale - item.product.price_buy) * item.stock
          )
          .reduce((a, b) => a + b, 0) ?? 0;
      return this.$options.filters.NumberFormat(total_price);
    },

    getProductTypeColor(productType) {
      switch (productType) {
        case "variant":
          return "blue";
        case "bundle":
          return "purple";
        case "no_stock_track":
          return "orange";
        case "regular":
        default:
          return "green";
      }
    },

    onSelectionChange(selectedItems) {
      this.internalSelectedItems = selectedItems;
      this.$emit("selection-change", selectedItems);
    },

    // Check if user has a specific permission
    checkPermission(permissionCode) {
      return hasPermission(permissionCode);
    },
    // Format helpers for permission-based display
    formatCost(value) {
      if (this.checkPermission("show_cost")) {
        return this.$options.filters.NumberFormat(value);
      }
      return "N/A";
    },
    formatProfit(value) {
      if (this.checkPermission("show_profit")) {
        return this.$options.filters.NumberFormat(value);
      }
      return "N/A";
    },
  },
};
</script>

<style scoped>
.ps-datatable-card {
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  background: #ffffff !important;
  border-radius: 16px !important;
}

.theme--dark .ps-datatable-card {
  border: 1px solid rgba(154, 140, 240, 0.16) !important;
  background: #1b1d30 !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  font-size: 10.5px !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
  text-transform: uppercase !important;
  color: #94a3b8 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 10px 14px !important;
  white-space: nowrap;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.theme--dark ::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  color: #7c82a0 !important;
  border-bottom: 1px solid rgba(154, 140, 240, 0.22) !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th span {
  font-size: 10.5px !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
  text-transform: uppercase !important;
  color: #94a3b8 !important;
}

.theme--dark ::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th span {
  color: #7c82a0 !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 9px 14px !important;
  border-bottom: 1px solid #eef0f6 !important;
  font-size: 13px !important;
  color: #141021 !important;
}

.theme--dark ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  border-bottom: 1px solid rgba(148, 154, 184, 0.12) !important;
  color: #eef0fa !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  background-color: rgba(91, 57, 201, 0.05) !important;
}

.theme--dark ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  background-color: rgba(154, 140, 240, 0.08) !important;
}

.hardware-table-name {
  max-width: 220px;
}

.hardware-table-model {
  max-width: 160px;
}
</style>
